// The shared theme library: a client's colours, saved once, worn by every app.
//
// Ported from the October hand-off's theme-library.js (design_handoff_nova/
// prototype). A saved theme is a client palette, not an app's token set.
// Every visitor app already speaks the same twelve roles (ground, ink, card,
// action and the three status colours), so one palette can be handed to any
// app and each maps it into its own variables. That mapping is the whole
// point: Registration, Wheel, Photo, Entry, BA and the Board never share a
// variable name, and they do share a client's colours.
//
// WHAT IS NOT HERE, ON PURPOSE. The prototype kept the library in the
// browser's localStorage; in the product a theme is a server record against
// the client, a client has a default, and a show has its own or wears that
// default. This module is the part every one of those places agrees on: the
// roles, the adapters, the contrast rule a save has to pass, and the order in
// which a show's theme is decided. It reads nothing and writes nothing, like
// the rest of this package.
//
// The prototype's sample palette (Halden Instruments) is not shipped; the
// house palette is, because it is Nova's own and every surface starts from it.

import { contrastRatio, parseHexColour, type ThemeToken } from "./theme.js";

/** The twelve roles, in the order an editor lists them, with the label and the help line. */
export const THEME_ROLES = [
  ["bg", "Ground", "The whole screen. Everything else is read against it."],
  ["ink", "Ink", "Body text and headings."],
  ["mute", "Quiet ink", "Help lines, captions and labels."],
  ["line", "Hairline", "Dividers and field borders."],
  ["card", "Card", "Panels, fields and anything raised off the ground."],
  ["sunk", "Sunk", "Wells, quiet fills and rows behind the card."],
  ["media", "Media well", "The reserved frame a photo or a QR lands in."],
  ["accent", "Secondary", "The action. One loud colour, nothing else."],
  ["accentInk", "On secondary", "The label sitting on the action."],
  ["ok", "Good", "Confirmations. Never the only signal."],
  ["warn", "Attention", "Doubt, rehearsal, a thing waiting."],
  ["bad", "Refusal", "A refusal or a fault."],
] as const;

export type ThemeRole = (typeof THEME_ROLES)[number][0];
export const THEME_ROLE_KEYS: readonly ThemeRole[] = THEME_ROLES.map((r) => r[0]);

export type ThemePalette = Readonly<Record<ThemeRole, string>>;

export const THEME_FACES = [
  ['"Geist","Montserrat",system-ui,sans-serif', "Geist"],
  ['"Montserrat",Helvetica,Arial,sans-serif', "Montserrat"],
  ["Arial,Helvetica,sans-serif", "Arial"],
  ['"Inter",system-ui,sans-serif', "Inter"],
] as const;

/** Corner styles: a control's, a box's and a field's. */
export const THEME_RADII = {
  pill: { ctl: "999px", box: "10px", field: "14px" },
  square: { ctl: "8px", box: "8px", field: "8px" },
} as const;
export type ThemeRadius = keyof typeof THEME_RADII;

export type LibraryTheme = {
  readonly id: string;
  readonly name: string;
  readonly radius: ThemeRadius;
  readonly headFont: string;
  readonly bodyFont: string;
  readonly light: ThemePalette;
  readonly dark: ThemePalette;
  /** Nova's own palette: listed, copied, never edited. */
  readonly locked?: boolean;
};

/** Nova's house palette, lifted from the values the surfaces already carry. */
export const HOUSE_THEME: LibraryTheme = {
  id: "th-nova",
  name: "Nova house",
  locked: true,
  radius: "pill",
  headFont: THEME_FACES[1][0],
  bodyFont: THEME_FACES[1][0],
  light: { bg: "#F6F6F5", ink: "#232323", mute: "#45433F", line: "#DEDCD8", card: "#FFFFFF", sunk: "#EFEDE8", media: "#E6E3DE", accent: "#232323", accentInk: "#F6F6F5", ok: "#2F6B3F", warn: "#8A6410", bad: "#8F3520" },
  dark: { bg: "#0A0A0A", ink: "#F6F6F5", mute: "#8D8B86", line: "#45433F", card: "#171716", sunk: "#1B1B1A", media: "#232322", accent: "#F6F6F5", accentInk: "#0A0A0A", ok: "#79C08B", warn: "#E6BE6A", bad: "#E28A74" },
};

/**
 * Which apps can wear a theme, and the vocabulary each speaks. An app with no
 * theme wears its own design system, which is the honest default: not every
 * show is themed.
 */
export const THEME_SURFACES = [
  ["reg", "Registration", "reg", "The web and onsite lanes"],
  ["wheel", "Wheel", "wheel", "The dial and the claim code"],
  ["board", "Leaderboard", "wheel", "Wall screen and in app"],
  ["photo", "Photo", "suite", "Booth capture and delivery"],
  ["entry", "Entry", "suite", "RSVP and the door"],
  ["ba", "BA", "suite", "The crew’s floor console"],
  ["demo", "Demo", "suite", "The product story on the stand screen"],
  ["kiosk", "Kiosk", "suite", "The hall screens"],
] as const;
export type ThemeSurfaceId = (typeof THEME_SURFACES)[number][0];
export type ThemeFamily = "reg" | "wheel" | "suite";

export function familyOf(surface: ThemeSurfaceId): ThemeFamily {
  return (THEME_SURFACES.find((s) => s[0] === surface)?.[2] ?? "suite") as ThemeFamily;
}

/** `pct` of `a` over `b`, as hex. The prototype painted a canvas; arithmetic gives the same channels. */
export function mixHex(a: string, b: string, pct: number): string {
  const x = parseHexColour(a);
  const y = parseHexColour(b);
  if (!x || !y) return a;
  const p = pct / 100;
  return (
    "#" +
    x
      .map((v, i) => Math.round(v * p + (y[i] as number) * (1 - p)))
      .map((v) => v.toString(16).padStart(2, "0"))
      .join("")
  );
}

export type ThemeAuditRow = {
  readonly label: string;
  readonly fg: string;
  readonly bg: string;
  readonly floor: number;
  readonly ratio: number;
  readonly ok: boolean;
};

/**
 * The pairs every theme has to survive, whatever app wears it. Text is held
 * to 4.5:1; the action against the ground is a shape, held to 3:1.
 *
 * JUDGED ON THE UNROUNDED RATIO, SHOWN TRUNCATED. This used to round to two
 * places and then compare, so a pair at 4.496:1 read as 4.5 and passed here
 * while checkThemeContrast, which compares the raw ratio, refused the same two
 * colours once `webThemeTokens` had put them in a bundle. A theme the library
 * accepts has to be a theme publish can ship, so both now use the raw number.
 * The shown figure is truncated rather than rounded for the same reason: a
 * refusal that says "4.5:1, under 4.5:1" reads as a bug.
 */
export function auditPalette(p: ThemePalette): ThemeAuditRow[] {
  const rows: [string, string, string, number][] = [
    ["Ink on ground", p.ink, p.bg, 4.5],
    ["Ink on a card", p.ink, p.card, 4.5],
    ["Quiet ink on ground", p.mute, p.bg, 4.5],
    ["Label on the action", p.accentInk, p.accent, 4.5],
    ["Good on a card", p.ok, p.card, 4.5],
    ["Attention on a card", p.warn, p.card, 4.5],
    ["Refusal on a card", p.bad, p.card, 4.5],
    ["Action against ground", p.accent, p.bg, 3],
  ];
  return rows.map(([label, fg, bg, floor]) => {
    const raw = contrastRatio(fg, bg) ?? 0;
    const ratio = Math.floor(raw * 100) / 100;
    return { label, fg, bg, floor, ratio, ok: raw >= floor };
  });
}

/**
 * Why a theme may not be saved, or nothing. A save is refused, rather than a
 * stand finding out, when any pair in either mode is under its floor, and when
 * a role is missing or is not a colour: a theme that cannot be measured cannot
 * be passed.
 */
export function themeSaveProblems(theme: Pick<LibraryTheme, "light" | "dark">): string[] {
  const problems: string[] = [];
  for (const mode of ["light", "dark"] as const) {
    const palette = theme[mode];
    const missing = THEME_ROLE_KEYS.filter((k) => !palette || !parseHexColour(palette[k] ?? ""));
    if (missing.length) {
      problems.push(`${mode}: ${missing.join(", ")} ${missing.length === 1 ? "is" : "are"} not a colour`);
      continue;
    }
    for (const row of auditPalette(palette)) {
      if (!row.ok) problems.push(`${mode}: ${row.label} is ${row.ratio}:1, under ${row.floor}:1`);
    }
  }
  return problems;
}

/** One palette, each app's vocabulary. Change an app's tokens and only its adapter moves. */
export const THEME_ADAPTERS = {
  suite(p: ThemePalette): Record<string, string> {
    return { ...p, okWash: mixHex(p.ok, p.bg, 14), warnWash: mixHex(p.warn, p.bg, 14), badWash: mixHex(p.bad, p.bg, 14) };
  },
  // Every colour name xp/visitor's ThemeScope paints for the wheel and the
  // board (COLOUR_VARS.wheel in themes.ts), so a palette handed over as
  // `overrides` leaves nothing of Brand showing through under a client's
  // ground. The last four were missing until publish started handing this to
  // a real dial: Brand's hairline and footer drawn on a client's green.
  // `secondary` is the registration adapter's mix, walked toward the ink until
  // it reads on both the ground and the field, for the reason `webThemeTokens`
  // gives below.
  wheel(p: ThemePalette): Record<string, string> {
    return {
      bg: p.bg,
      ink: p.ink,
      accent: p.accent,
      accentInk: p.accentInk,
      logo: p.ink,
      field: p.card,
      fieldInk: p.ink,
      secondary: firstPassing(mixHex(p.ink, p.bg, 82), p.ink, reads(4.5, p.bg, p.card)),
      line: p.line,
      footer: p.sunk,
      data: p.accent,
    };
  },
  reg(p: ThemePalette): Record<string, string> {
    return {
      canvas: p.bg,
      card: p.card,
      wash: p.sunk,
      line: p.line,
      lineSoft: mixHex(p.line, p.card, 55),
      lineStrong: p.mute,
      ink: p.ink,
      secondary: mixHex(p.ink, p.bg, 82),
      mute: p.mute,
      primary: p.accent,
      onPrimary: p.accentInk,
      button: p.card,
      data: p.accent,
      tint: p.sunk,
      attention: p.warn,
      attentionWash: mixHex(p.warn, p.bg, 12),
      attentionLine: mixHex(p.warn, p.bg, 45),
      footer: p.sunk,
    };
  },
  console(p: ThemePalette): Record<string, string> {
    return {
      canvas: p.bg,
      rail: p.card,
      card: p.card,
      cell: p.card,
      wash: p.sunk,
      hover: mixHex(p.sunk, p.bg, 70),
      line: p.line,
      lineStrong: p.mute,
      ink: p.ink,
      secondary: mixHex(p.ink, p.bg, 82),
      mute: p.mute,
      primary: p.accent,
      onPrimary: p.accentInk,
      select: p.accent,
      navOn: p.accent,
      onSelect: p.accentInk,
      tint: p.sunk,
      attention: p.warn,
      onAttention: p.bg,
      attentionWash: mixHex(p.warn, p.bg, 12),
      attentionLine: mixHex(p.warn, p.bg, 45),
      good: p.ok,
      goodWash: mixHex(p.ok, p.bg, 12),
      bad: p.bad,
      badWash: mixHex(p.bad, p.bg, 12),
    };
  },
} as const;

/** What an app reads from a theme, in its own vocabulary. */
export function themeTokens(theme: LibraryTheme, surface: ThemeSurfaceId, mode: "light" | "dark"): Record<string, string> {
  return THEME_ADAPTERS[familyOf(surface)](theme[mode]);
}

/** The colour names `THEME_ADAPTERS.wheel` emits: what a wheel or board `overrides` may carry. */
export const WHEEL_OVERRIDE_KEYS: readonly string[] = Object.keys(THEME_ADAPTERS.wheel(HOUSE_THEME.light));

/**
 * The first colour on the way from `from` to `to` that `passes`, in five per
 * cent steps, ending on `to` itself.
 *
 * WHY A WALK AND NOT A FIXED MIX. A library theme is twelve roles, and the
 * pages that wear it read a few colours the roles do not name: a second ink
 * weight, a field border, a wash under a chosen option. Those are derived, and
 * a derived colour has not been through the save rule. A fixed mix that reads
 * on Nova house can miss on a client's palette, and then a theme the library
 * accepted is one checkThemeContrast refuses at publish. So each derived colour
 * starts where the prototype put it and moves toward a role the save rule has
 * already measured until it reads. The end of the walk always passes, because
 * it is that role, so a theme that passes `themeSaveProblems` always yields a
 * token set that passes checkThemeContrast. check.mjs holds that over palettes
 * nobody chose.
 */
function firstPassing(from: string, to: string, passes: (colour: string) => boolean): string {
  for (let pct = 0; pct <= 100; pct += 5) {
    const colour = mixHex(to, from, pct);
    if (passes(colour)) return colour;
  }
  return to;
}

/** A colour test: at least `floor` against every one of `grounds`. */
function reads(floor: number, ...grounds: string[]): (colour: string) => boolean {
  return (colour) => grounds.every((ground) => (contrastRatio(colour, ground) ?? 0) >= floor);
}

/**
 * Depth and the modal veil, per mode. Not roles: a palette does not choose
 * them, and Brand's own are the right ones on any ground of the same
 * lightness. The light shadow is Brand's `--xp-shadow`, the light scrim is
 * tokens.css's, and the dark pair is deeper because a shadow tuned for a white
 * card reads as dirt on a dark one.
 */
const WEB_DEPTH = {
  light: { shadow: "-2px 3px 8px rgba(26,26,26,.10)", scrim: "rgba(26, 26, 26, 0.55)" },
  dark: { shadow: "-2px 3px 8px rgba(0,0,0,.45)", scrim: "rgba(0, 0, 0, 0.7)" },
} as const;

/**
 * The corner a box and a field take on the web page. 8px under both styles:
 * Brand's fields and ticks are 8px under its pills (xp/visitor SHAPES
 * reg-brand fieldRadius), and XP1's square system is 8px throughout (reg-xp1).
 * THEME_RADII.pill.field is 14px, which is the prototype's suite corner and is
 * not what the registration page draws.
 */
const WEB_BOX_RADIUS = "8px";

/**
 * A library theme as the registration page's own tokens: the closed `--xp-*`
 * set in theme.ts, every one of them, for one mode.
 *
 * THE WEB ADAPTER, BESIDE `reg` AND NOT INSTEAD OF IT. `THEME_ADAPTERS.reg`
 * speaks the October prototype's names (canvas, primary, onPrimary), which is
 * what the console's editor draws with. The page a visitor opens, and the
 * tablet's capture surface, read `--xp-*`, and a signed bundle's `theme` and
 * `theme_dark` are refused by checkTheme unless every key is on
 * THEMEABLE_TOKENS. So this is what publish writes into those two keys when a
 * show's Registration wears a library theme.
 *
 * COMPLETE, NOT AN OVERRIDE. The console's raw-token editor sent only the
 * tokens somebody changed, and bundle.ts says why that left contrast
 * unjudgeable: a partial set has to be measured against defaults that live in
 * a stylesheet. This is all twenty-three, so publish can run
 * checkThemeContrast on exactly what it signs.
 *
 * The derived colours (the second and third ink, the field border, the tint)
 * are walked by `firstPassing` until they read; the three washes are the
 * registration adapter's 12% mix of the verdict over the ground.
 */
export function webThemeTokens(theme: LibraryTheme, mode: "light" | "dark"): Record<ThemeToken, string> {
  const p = theme[mode];
  return {
    "--xp-bg": p.bg,
    "--xp-surface": p.card,
    "--xp-surface-2": p.sunk,
    "--xp-line": p.line,
    "--xp-line-strong": firstPassing(p.mute, p.ink, reads(3, p.card)),
    "--xp-ink": p.ink,
    "--xp-ink-2": firstPassing(mixHex(p.ink, p.bg, 82), p.ink, reads(4.5, p.bg, p.card)),
    "--xp-ink-3": firstPassing(p.mute, p.ink, reads(4.5, p.bg, p.card)),
    "--xp-accent": p.accent,
    "--xp-accent-ink": p.accentInk,
    "--xp-data": p.accent,
    // A ground rather than an ink, so the walk is toward the card, which the
    // save rule has already measured the ink against.
    "--xp-tint": firstPassing(p.sunk, p.card, (tint) => (contrastRatio(p.ink, tint) ?? 0) >= 4.5),
    "--xp-good": p.ok,
    "--xp-good-soft": mixHex(p.ok, p.bg, 12),
    "--xp-warn": p.warn,
    "--xp-warn-soft": mixHex(p.warn, p.bg, 12),
    "--xp-bad": p.bad,
    "--xp-bad-soft": mixHex(p.bad, p.bg, 12),
    "--xp-radius": THEME_RADII[theme.radius].ctl,
    "--xp-radius-sm": WEB_BOX_RADIUS,
    "--xp-sans": theme.bodyFont,
    "--xp-shadow": WEB_DEPTH[mode].shadow,
    "--xp-scrim": WEB_DEPTH[mode].scrim,
  };
}

/**
 * The way back: an app's own token set read as the twelve roles. A theme
 * usually starts as somebody editing one app, so the colours that app does not
 * carry are taken from the house palette rather than invented.
 */
export function paletteFromSurface(surface: ThemeSurfaceId, v: Readonly<Record<string, string>>, mode: "light" | "dark"): ThemePalette {
  const house = HOUSE_THEME[mode];
  const family = familyOf(surface);
  const get = (k: string, fallback: string) => v[k] ?? fallback;
  if (family === "wheel") {
    const bg = get("bg", house.bg);
    const ink = get("ink", house.ink);
    const field = get("field", house.card);
    return {
      ...house,
      bg,
      ink,
      mute: mixHex(ink, bg, 72),
      line: mixHex(ink, bg, 18),
      card: field,
      sunk: mixHex(field, bg, 60),
      media: mixHex(field, bg, 40),
      accent: get("accent", house.accent),
      accentInk: get("accentInk", house.accentInk),
    };
  }
  if (family === "reg") {
    return {
      ...house,
      bg: get("canvas", house.bg),
      ink: get("ink", house.ink),
      mute: get("mute", house.mute),
      line: get("line", house.line),
      card: get("card", house.card),
      sunk: get("wash", house.sunk),
      media: get("wash", house.media),
      accent: get("primary", house.accent),
      accentInk: get("onPrimary", house.accentInk),
      warn: get("attention", house.warn),
    };
  }
  return { ...house, ...v } as ThemePalette;
}

/**
 * Which theme an app at a show wears, or null for the app's own design system.
 *
 * In this order, as the hand-off decides it: an app pointed at a theme (or at
 * its own system) on purpose wins; otherwise the show's theme; otherwise the
 * client's default, so a client who comes back does not start from nothing.
 * `own` in `surfaceThemes` is the explicit "this app wears its own system".
 */
export const OWN_SYSTEM = "own";

export function resolveShowTheme(input: {
  readonly surface: ThemeSurfaceId;
  readonly surfaceThemes?: Readonly<Partial<Record<ThemeSurfaceId, string>>> | null;
  readonly showTheme?: string | null;
  readonly clientDefault?: string | null;
}): string | null {
  const pointed = input.surfaceThemes?.[input.surface];
  if (pointed === OWN_SYSTEM) return null;
  if (pointed) return pointed;
  if (input.showTheme) return input.showTheme;
  if (input.clientDefault) return input.clientDefault;
  return null;
}
